import { Button, CodeEntry, Grid, Text } from '@afterpaytouch/core'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { clearAuthSessionsFlow, useAppDispatch, useIsCashApp, useSubmitEmailCode } from '../../state'
import { CashSignInTerms } from '../CashSignInTerms'
import { UsFebConsentCopy, useUsFebConsentCopy } from '../UsFebConsentCopy'
import { clearUsFebConsentCopyDisplayed } from '../../flow/one-offer-page/consentCopySignal'
import { useTranslation } from '../../utils/convergence'
import { useCheckoutSizes } from '../../utils/responsive'
import { ResendEmailCode } from './ResendEmailCode'

interface LoginEmailCodeProps {
  email: string
  codeLength: number
  onAccepted: (handoffRef?: string) => Promise<void>
  onClosed: () => void
  onDeclined: () => void
}

export const LoginEmailCode: FunctionComponent<LoginEmailCodeProps> = ({ email, codeLength, onAccepted, onClosed, onDeclined }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { textSize } = useCheckoutSizes()
  const submitEmailCode = useSubmitEmailCode()
  const isCashApp = useIsCashApp()
  const showUsFebConsent = useUsFebConsentCopy()
  const entryRef = useRef<{ focus: () => void }>(null)
  const [submitting, setSubmitting] = useState(false)
  const [errorText, setErrorText] = useState<string>()
  const [entryKey, setEntryKey] = useState(0)

  useEffect(() => {
    entryRef.current?.focus()
  }, [entryKey])

  useEffect(
    () => () => {
      dispatch(clearAuthSessionsFlow())
    },
    [dispatch]
  )

  const onNotYou = (): void => {
    clearUsFebConsentCopyDisplayed()
    dispatch(clearAuthSessionsFlow())
  }

  const onCodeClosed = (): void => {
    clearUsFebConsentCopyDisplayed()
    onClosed()
  }

  const onComplete = async (code: string): Promise<void> => {
    setSubmitting(true)
    const { outcome, handoffRef } = await submitEmailCode(code)
    if (outcome === 'ACCEPTED') {
      return await onAccepted(handoffRef)
    }
    if (outcome === 'DECLINED') {
      return onDeclined()
    }
    if (outcome === 'CLOSED') {
      return onCodeClosed()
    }
    setSubmitting(false)
    setEntryKey(entryKey + 1)
    if (outcome === 'SUPERSEDED') {
      setErrorText(undefined)
      return
    }
    setErrorText(outcome === 'REJECTED' ? t('verify:error.incorrectResetCode') : t('verify:error.default'))
  }

  const onSent = (): void => {
    setEntryKey((key) => key + 1)
    setErrorText(undefined)
  }

  return (
    <>
      <Grid>
        <MaskedElement actionName='Email Code Description'>
          <Text size={textSize}>{t('verify:email.description', { email })}</Text>
        </MaskedElement>
      </Grid>
      <Grid>
        <MaskedElement actionName='Email Code Input'>
          <CodeEntry
            key={entryKey}
            ref={entryRef}
            codeEntryLength={codeLength}
            onComplete={onComplete}
            disabled={submitting}
            error={errorText !== undefined}
            errorText={errorText}
            testNameSpace='login-email-code'
            aria-label={t('verify:heading')}
          />
        </MaskedElement>
      </Grid>
      <ResendEmailCode email={email} disabled={submitting} onSent={onSent} onClosed={onCodeClosed} />
      <Grid>
        <Button.Link type='button' testNameSpace='login-email-code-not-you' disabled={submitting} onClick={onNotYou}>
          {t('common:notYou')}
        </Button.Link>
      </Grid>
      {isCashApp && <CashSignInTerms />}
      {showUsFebConsent && (
        <Grid>
          <UsFebConsentCopy />
        </Grid>
      )}
    </>
  )
}
